import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';

type HeatmapHistoryResult = {
	history: GetStatsResponseHistoryData[];
	interval?: unknown;
};
type HeatmapRangeFetcher = (
	controls: ChartControlsDataInterface,
) => Promise<HeatmapHistoryResult | null>;

const MAX_CONCURRENT_REQUESTS = 3;
const LONG_RANGE_DAYS = 35;

const buildMonthlyControls = (controls: ChartControlsDataInterface): ChartControlsDataInterface[] => {
	const ranges: ChartControlsDataInterface[] = [];
	const requestedFrom = controls.time.from;
	const requestedTo = controls.time.to;
	let cursor = new Date(requestedFrom.getFullYear(), requestedFrom.getMonth(), 1, 0, 0, 0, 0);

	while (cursor.getTime() <= requestedTo.getTime()) {
		const monthStart = new Date(Math.max(cursor.getTime(), requestedFrom.getTime()));
		const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59, 999);

		ranges.push({
			...controls,
			time: {
				from: monthStart,
				to: new Date(Math.min(monthEnd.getTime(), requestedTo.getTime())),
			},
		});

		cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
	}

	return ranges;
};

const isLongRange = (controls: ChartControlsDataInterface): boolean => {
	const requestedDays =
		(controls.time.to.getTime() - controls.time.from.getTime()) / (24 * 60 * 60 * 1000);
	return requestedDays > LONG_RANGE_DAYS;
};

const fetchInBatches = async (
	ranges: ChartControlsDataInterface[],
	fetchRange: HeatmapRangeFetcher,
): Promise<GetStatsResponseHistoryData[]> => {
	const history: GetStatsResponseHistoryData[] = [];

	for (let index = 0; index < ranges.length; index += MAX_CONCURRENT_REQUESTS) {
		const batch = ranges.slice(index, index + MAX_CONCURRENT_REQUESTS);
		const results = await Promise.all(batch.map((range) => fetchRange(range)));
		results.forEach((result) => history.push(...(result?.history ?? [])));
	}

	return history.sort((a, b) => a.datetime.localeCompare(b.datetime));
};

/**
 * Fetches a full calendar range without chart-only last-hour patching.
 * Long ranges are always split into month-sized requests because the statistics
 * API may return a plausible-looking but incomplete/coarsened yearly response.
 */
export const fetchCompleteEnergyHeatmapHistory = async (
	controls: ChartControlsDataInterface,
	fetchRange: HeatmapRangeFetcher,
): Promise<GetStatsResponseHistoryData[]> => {
	if (!isLongRange(controls)) return (await fetchRange(controls))?.history ?? [];

	const monthlyHistory = await fetchInBatches(buildMonthlyControls(controls), fetchRange);
	return monthlyHistory;
};
